/**
 * Hvilke sensorer en blomsterkasse faktisk har.
 *
 * `potter.sensorer` (text[]) lister dem: jord1–jord4 (probeplassene, GPIO
 * 34/35/32/33), vann (VL53L0X-laseren), vann2 (laser 2, høyre skuff i 2.0 stor),
 * klima (DHT22/SHT41) og kamera (ESP32-CAM).
 * NULL betyr «ikke valgt ennå»: da gjelder `har_sensorer` for alle som fantes før
 * valget kom (alt utenom vann2), slik appen virket da. Har kassa ingen sensorer,
 * er `har_sensorer` false. Laser 2 vises bare når den er valgt.
 *
 * Sensorer som ikke finnes, nulles i avlesningene (maskerAvlesning). Da viser
 * appen dem ikke, og varsler og diagnoser ser dem heller ikke. En probe som
 * står i lufta fordi den aldri ble koblet til, gir altså ikke «probe svarer ikke».
 */

export const SENSORER = ['jord1', 'jord2', 'jord3', 'jord4', 'vann', 'vann2', 'klima', 'kamera'] as const;
export type SensorNavn = (typeof SENSORER)[number];
/** Det NULL betyr: alle sensorene unntatt laser 2, som kom med 2.0 stor. */
export const STANDARD_SENSORER: readonly SensorNavn[] = SENSORER.filter((s) => s !== 'vann2');

export interface SensorOppsett {
  /** Probeplass 1–4 (indeks 0–3) = jord1–jord4 = seksjon 1–4. */
  jord: [boolean, boolean, boolean, boolean];
  /** Laser 1: tanken, eller venstre skuff når kassa også har vann2. */
  vann: boolean;
  /** Laser 2: høyre skuff i 2.0 stor. */
  vann2: boolean;
  klima: boolean;
  kamera: boolean;
}

export function sensorOppsett(p: { har_sensorer: boolean; sensorer?: string[] | null }): SensorOppsett {
  const liste: readonly string[] = !p.har_sensorer ? [] : (p.sensorer ?? STANDARD_SENSORER);
  const har = (s: SensorNavn) => liste.includes(s);
  return {
    jord: [har('jord1'), har('jord2'), har('jord3'), har('jord4')],
    vann: har('vann'),
    vann2: har('vann2'),
    klima: har('klima'),
    kamera: har('kamera'),
  };
}

/** Oppsettet som liste til databasen. */
export function sensorListe(o: SensorOppsett): SensorNavn[] {
  const ut: SensorNavn[] = [];
  o.jord.forEach((j, i) => j && ut.push(`jord${i + 1}` as SensorNavn));
  if (o.vann) ut.push('vann');
  if (o.vann2) ut.push('vann2');
  if (o.klima) ut.push('klima');
  if (o.kamera) ut.push('kamera');
  return ut;
}

export function harNoenSensor(o: SensorOppsett): boolean {
  return o.jord.some(Boolean) || o.vann || o.vann2 || o.klima || o.kamera;
}

/**
 * Har planteplassen (seksjon 1–4) en jordprobe? En delt potte har én probe per
 * felt. En udelt potte bruker fremre seksjon, og da teller begge probene i potta,
 * samme regel som feltFukter.
 */
export function feltHarProbe(seksjon: number, delt: boolean, o: SensorOppsett): boolean {
  const i = seksjon - 1;
  return delt ? o.jord[i]! : o.jord[i]! || !!o.jord[i + 1];
}

type Avlesning = {
  jord1?: number | null;
  jord2?: number | null;
  jord3?: number | null;
  jord4?: number | null;
  vann_avstand_mm?: number | null;
  vann2_avstand_mm?: number | null;
  temperatur?: number | null;
  luftfuktighet?: number | null;
};

/**
 * Nuller feltene til sensorer kassa ikke har. Returnerer samme objekt når
 * ingenting skal nulles, så Svelte ikke ser en ny verdi hver gang.
 */
export function maskerAvlesning<T extends Avlesning>(rad: T, o: SensorOppsett): T;
export function maskerAvlesning<T extends Avlesning>(rad: T | null | undefined, o: SensorOppsett): T | null | undefined;
export function maskerAvlesning<T extends Avlesning>(rad: T | null | undefined, o: SensorOppsett): T | null | undefined {
  if (!rad) return rad;
  const av: (keyof Avlesning)[] = [];
  o.jord.forEach((j, i) => !j && av.push(`jord${i + 1}` as keyof Avlesning));
  if (!o.vann) av.push('vann_avstand_mm');
  if (!o.vann2) av.push('vann2_avstand_mm');
  if (!o.klima) av.push('temperatur', 'luftfuktighet');
  const skal = av.filter((k) => k in rad && rad[k] != null);
  if (skal.length === 0) return rad;
  const ut = { ...rad };
  for (const k of skal) (ut as Avlesning)[k] = null;
  return ut;
}
